import React, { useEffect, useState } from "react";
import { AccountBalanceResult, AccountRole, BetStatus, Currency, DataPoint, DataSet, TimeFrame} from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { MetricsValuePanel } from "../metrics/MetricsValuePanel";

interface Props {
  userId: string;
  accounts?: AccountBalanceResult[]
}

interface BonusSummary {
  name: string;
  currency: string;
  currentMonth?: string | number;
  lastMonth?: string |number;
}

const UserMetrics: React.FC<Props> = (props: Props) => {
  const metricsService = getContainer().getMetricsService();
  const sportbetsService = getContainer().getSportBetsService();
  const accountService = getContainer().getAccountService();
  const date = new Date();
  const dateString = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).toISOString().slice(0, 10);
  const startOfThisMonth = new Date(date.getFullYear(), date.getUTCMonth(), 1).toISOString().slice(0, 10);
  const startOfLastMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1).toISOString().slice(0, 10);
  const userId = props.userId;
  const accounts = props.accounts;
  const [selectedCurrency, setSelectedCurrency] = useState(localStorage.getItem("activeCurrency") || "");
  const [thisMonthlyDeposit, setThisMonthlyDeposit] = useState(0);
  const [lastMonthlyDeposit, setLastMonthlyDeposit] = useState(0);
  const [thisMonthlyWithdraw, setThisMonthlyWithdraw] = useState(0);
  const [lastMonthlyWithdraw, setLastMonthlyWithdraw] = useState(0);
  const [bonusSummaries, setBonusSummaries] = useState({});
  const [thisMonthlyPendingBalanceMain, setThisMonthlyPendingBalanceMain] = useState(0);
  const [thisMonthlyPendingBalanceBonus, setThisMonthlyPendingBalanceBonus] = useState(0);
  const [lifetimePendingBalanceMain, setLifetimePendingBalanceMain] = useState(0);
  const [lifetimePendingBalanceBonus, setLifetimePendingBalanceBonus] = useState(0);
  
  const getPreviousAndCurrentValues = (data: any, timeFrame : TimeFrame) => {
  
    const LAST_MONTH = "LAST_MONTH";
  
    const previousAndCurrentValues = {"previous": 0, "current": 0};
  
    if(timeFrame === TimeFrame.PREVIOUS_AND_CURRENT_MONTH){
      
      data.forEach( (element : any) => {
        if(element.key === LAST_MONTH){
          previousAndCurrentValues.previous = element.value;
        }else{
          previousAndCurrentValues.current = element.value;
        }
      });
    }
  
    return previousAndCurrentValues;
  
  }

  const calculateMonthly = (data: DataSet, isLastMonth?: boolean) => {
    let result: number = 0;
    let currentMonth: number = new Date().getUTCMonth();
    let lastMonth: number = new Date().getUTCMonth() - 1;
    isLastMonth = isLastMonth || false;
    if (data === null || data === undefined) {
      return Number(Big(result).round(4));
    }
    data.points!.forEach((point : DataPoint) => {
      let [year, month, day] = point.date!.split("-");
      let date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
      let value = Number(point.value!);
      if((isLastMonth && date.getUTCMonth() === lastMonth)
      ||(!isLastMonth && date.getUTCMonth() === currentMonth)){
        result = result + value;
      }
    });

    if (result === 0) {
      return 0;
    }
    return parseFloat(Number(result).toFixed(5));
  }

  const addOrUpdateItem = (item: BonusSummary) => {
    setBonusSummaries((prevData: any) => ({ ...prevData, [item.name]: item }));
  }

  const getUserBetSummaryMain = async(userId:string, selectedCurrency:string, startOfThisMonth:string, dateString:string )=>{
    await sportbetsService.getUserBetSummary({
      userId: Number(userId),
      currency: selectedCurrency,
      statuses: [BetStatus.PENDING, BetStatus.REQUESTED, BetStatus.APPROVED, BetStatus.UNGRADED],
      riskAccountRoles: [AccountRole.MAIN],
      startDate: new Date(startOfThisMonth), endDate: new Date(dateString)
    })
      .then((data: any) => {
        setThisMonthlyPendingBalanceMain(data! && data!.items![0]!.sumOfRisk!);
      })
      .catch(e => {
        console.error("Error gettting metrics for Withdraw endpoint");
        setThisMonthlyPendingBalanceMain(NaN);
      });

    await sportbetsService.getUserBetSummary({
      userId: Number(userId),
      currency: selectedCurrency,
      statuses: [BetStatus.PENDING, BetStatus.REQUESTED, BetStatus.APPROVED, BetStatus.UNGRADED],
      riskAccountRoles: [AccountRole.MAIN]
    })
      .then((data: any) => {
        setLifetimePendingBalanceMain(data! && data!.items![0]!.sumOfRisk!);
      })
      .catch(e => {
        console.error("Error gettting metrics for Withdraw endpoint");
        setLifetimePendingBalanceMain(NaN);
      });
  }

  const getUserBetSummaryBonus= async(userId:string, selectedCurrency:string, startOfThisMonth:string, dateString:string )=>{
    await sportbetsService.getUserBetSummary({
      userId: Number(userId),
      currency: selectedCurrency,
      statuses: [BetStatus.PENDING, BetStatus.REQUESTED, BetStatus.APPROVED, BetStatus.UNGRADED],
      riskAccountRoles: [AccountRole.BONUS],
      startDate: new Date(startOfThisMonth), endDate: new Date(dateString)
    })
      .then((data: any) => {
        setThisMonthlyPendingBalanceBonus(data! && data!.items![0]!.sumOfRisk!);
      })
      .catch(e => {
        console.error("Error gettting metrics for Withdraw endpoint");
        setThisMonthlyPendingBalanceBonus(NaN);
      });
  
    await sportbetsService.getUserBetSummary({
      userId: Number(userId),
      currency: selectedCurrency,
      statuses: [BetStatus.PENDING, BetStatus.REQUESTED, BetStatus.APPROVED, BetStatus.UNGRADED],
      riskAccountRoles: [AccountRole.BONUS]
    })
      .then((data: any) => {
        setLifetimePendingBalanceBonus(data! && data!.items![0]!.sumOfRisk!);
      })
      .catch(e => {
        console.error("Error gettting metrics for Withdraw endpoint");
        setLifetimePendingBalanceBonus(NaN);
      });
  }

  const getData = async (userId: any, selectedCurrency:string, accounts: AccountBalanceResult[]) => {
    if(!selectedCurrency) return;

    await metricsService.getUserEventDailySum("DEPOSIT", TimeFrame.PREVIOUS_AND_CURRENT_MONTH, selectedCurrency, "MAIN", userId)
      .then((depositData: any) => {
        const values = getPreviousAndCurrentValues(depositData.data, TimeFrame.PREVIOUS_AND_CURRENT_MONTH);
        setThisMonthlyDeposit(values.current);
        setLastMonthlyDeposit(values.previous);
      })
      .catch(e => {
        console.error("Error getting metrics for Deposit endpoint");
        setThisMonthlyDeposit(NaN);
        setLastMonthlyDeposit(NaN);
      });
    await metricsService.getUserEventDailySum("WITHDRAW", TimeFrame.PREVIOUS_AND_CURRENT_MONTH, selectedCurrency,"MAIN", userId)
      .then((withdrawData: any) => {
        const values = getPreviousAndCurrentValues(withdrawData.data, TimeFrame.PREVIOUS_AND_CURRENT_MONTH);
        setThisMonthlyWithdraw(values.current);
        setLastMonthlyWithdraw(values.previous);
      })
      .catch(e => {
        console.error("Error getting metrics for Withdraw endpoint");
        setThisMonthlyWithdraw(NaN);
        setLastMonthlyWithdraw(NaN);
      });
      

    accounts.forEach((account : AccountBalanceResult) => {
      metricsService.getUserEventDailySum("BONUS", TimeFrame.PREVIOUS_AND_CURRENT_MONTH, selectedCurrency, account.accountAttributes!["ROLE"], userId)
      .then((bonusData: any) => {
        const values = getPreviousAndCurrentValues(bonusData.data, TimeFrame.PREVIOUS_AND_CURRENT_MONTH);

        addOrUpdateItem({ 
          name: account.accountAttributes!["ROLE"], 
          currency: selectedCurrency, 
          currentMonth: values.current, 
          lastMonth: values.previous 
        });
      })
      .catch(e => {
        console.error("Error getting metrics for Withdraw endpoint");
        addOrUpdateItem({
          name: account.accountAttributes!["ROLE"],
          currency: selectedCurrency,
          currentMonth: NaN,
          lastMonth: NaN          
        });
      });
    });
  }

  useEffect(() => {
    if(!selectedCurrency){
      accountService.getCurrencies()
        .then((currencies: Currency[])=>{
          if(currencies && currencies.length){
            setSelectedCurrency(currencies[0].code!)
          }
        })
    }
  }, [selectedCurrency]);

  useEffect(() => {
    if (!selectedCurrency) return;
    getUserBetSummaryMain(userId, selectedCurrency, startOfThisMonth, dateString );
    getUserBetSummaryBonus(userId, selectedCurrency, startOfThisMonth, dateString );
  }, [userId, selectedCurrency]);

  useEffect(() => {
    if (!selectedCurrency) return;
    if (!accounts) return;
    getData(userId, selectedCurrency, accounts!);
  }, [userId, selectedCurrency, accounts]);

  return (
    <div>
      <div className="row">
      <MetricsValuePanel
          header="Pending Main"
          mainTitle={"This Month"}
          subTitle={"Lifetime"}
          mainValue={thisMonthlyPendingBalanceMain || 0}
          subValue={lifetimePendingBalanceMain || 0 }
          currency={selectedCurrency}
          type="PENDING"
          metricPanelSize="col-md-6 col-xl-6"
          color="BLUE" 
          navTo={"/sportsbetting/bets/list"}
          listRequest={{ statuses: ["PENDING", "REQUESTED", "APPROVED", "UNGRADED"], userId: userId, lookupExpanded: true }}
          />

        <MetricsValuePanel
          header="Pending Bonus"
          mainTitle={"This Month"}
          subTitle={"Lifetime"}
          mainValue={thisMonthlyPendingBalanceBonus || 0 }
          subValue={lifetimePendingBalanceBonus || 0}
          currency={selectedCurrency}
          type="PENDING"
          metricPanelSize="col-md-6 col-xl-6"
          color="BLUE" 
          navTo={"/sportsbetting/bets/list"}
          listRequest={{ statuses: ["PENDING", "REQUESTED", "APPROVED", "UNGRADED"], userId: userId, lookupExpanded: true }}
          />

        <MetricsValuePanel
          header="Deposits"
          mainTitle={"This Month"}
          subTitle={"Last Month"}
          mainValue={thisMonthlyDeposit}
          subValue={lastMonthlyDeposit}
          currency={selectedCurrency}
          type="DEPOSIT"
          metricPanelSize="col-md-6 col-xl-6"
          color="GREEN" 
          navTo={"/analytics-logs"}
          listRequest={{ types: ["DEPOSIT"], userId: userId, startDate: startOfLastMonth, endDate: dateString, lookupExpanded: true }}
          />
        <MetricsValuePanel
          header="Withdraw"
          mainTitle={"This Month"}
          subTitle={"Last Month"}
          mainValue={thisMonthlyWithdraw}
          subValue={lastMonthlyWithdraw}
          currency={selectedCurrency}
          type="WITHDRAW"
          metricPanelSize="col-md-6 col-xl-6"
          color="RED" 
          navTo={"/analytics-logs"}
          listRequest={{ types: ["WITHDRAWAL"], userId: userId, startDate: startOfLastMonth, endDate: dateString, lookupExpanded: true }}
        />

        {Object.values(bonusSummaries).map((bonusSummary: any) => {
          return (
              <MetricsValuePanel
                header={ accounts!.length > 1 ? `Bonus to ${bonusSummary.name}` : `Bonus`}
                mainTitle={"This Month"}
                subTitle={"Last Month"}
                mainValue={bonusSummary.currentMonth}
                subValue={bonusSummary.lastMonth}
                currency={selectedCurrency}
                type="BONUS"
                metricPanelSize="col-md-6 col-xl-6"
                color="YELLOW" 
                navTo={"/analytics-logs"}
                listRequest={{ types: ["BONUS"], userId: userId, startDate: startOfLastMonth, endDate: dateString, lookupExpanded: true }}
              />
          )
        })}
      </div>
    </div>
  );
}
export default UserMetrics;